import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import {
    Popper,
    Paper,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Typography,
    ClickAwayListener,
    CircularProgress,
    Box,
    IconButton,
    InputBase,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import "./NavBarSearch.scss";
import { ThemeContext } from "@emotion/react";
import getTheme from "../../theme";

const NavBarSearch = () => {
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [results, setResults] = useState([]);
    const [anchorEl, setAnchorEl] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const intl = useIntl();
    // const theme = getTheme();

    useEffect(() => {
        if (searchQuery.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }
        const fetchProducts = async () => {
            setLoading(true);
            try {
                const res = await fetch(
                    `${process.env.REACT_APP_API_URL}/products?filters[title][$containsi]=${encodeURIComponent(searchQuery)}&populate=*`
                );
                const json = await res.json();
                setResults(json.data || []);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        };
        fetchProducts();
    }, [searchQuery]);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
        setSearchOpen(true);
    };
    const handleClose = () => {
        setSearchOpen(false);
        setSearchQuery("");
        setResults([]);
        setAnchorEl(null);
    };

    return (
        <ClickAwayListener onClickAway={handleClose}>
            <Box sx={{ position: "relative", display: "inline-block" }}>
                {/* Search icon button triggers popup */}
                <IconButton onClick={handleOpen} aria-label={intl.formatMessage({ id: "search", defaultMessage: "Szukaj" })}>
                    <SearchIcon />
                </IconButton>

                {/* Popup search box */}
                <Popper
                    open={searchOpen}
                    anchorEl={anchorEl}
                    placement="bottom-end"
                    sx={{ zIndex: 1300, width: 380, maxWidth: "calc(100vw - 24px)" }}
                >
                    <Paper className="search-paper" sx={{ mt: "18px", p: "20px 24px", border: "1px solid", borderColor: "divider", boxShadow: 'none', bgcolor: "background.default" }}>
                        {/* Search input with close button */}
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                bgcolor: "background.default",
                                borderBottom: "1px solid",
                                borderColor: "text.primary",
                                pb: "6px",
                            }}
                        >
                            <InputBase className="search-input"
                                placeholder={intl.formatMessage({ id: "search_placeholder", defaultMessage: "Szukaj zapachów…" })}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{
                                    flex: 1,
                                    border: "none",
                                    fontFamily: "var(--font-serif)",
                                    fontSize: "1.25rem",
                                    fontWeight: 300,
                                    bgcolor: "background.default",
                                    "&input": {
                                        padding: "6px 0",

                                        ":focus": { borderColor: "white" },
                                        // border: "1px solid #ccc",
                                        width: "100%",


                                    },
                                    "&input:focus": {
                                        outline: "none",
                                        color: "text.primary",
                                    },
                                }}
                                inputProps={{ "aria-label": intl.formatMessage({ id: "search_placeholder", defaultMessage: "Szukaj zapachów…" }) }}
                            />
                            <IconButton onClick={handleClose} aria-label={intl.formatMessage({ id: "close", defaultMessage: "Zamknij" })} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>


                        {/* Loading indicator */}
                        {loading && (
                            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {/* Results list */}
                        <List dense sx={{ maxHeight: 300, overflowY: "auto" }}>
                            {results.length === 0 && !loading && (
                                <Typography variant="body2" sx={{ p: 1, textAlign: "center" }}>

                                </Typography>
                            )}
                            {results.map((item) => {
                                const product = item || {};
                                const thumbnailUrl =
                                    product?.img1?.formats?.thumbnail?.url ||
                                    product?.img2?.formats?.thumbnail?.url ||
                                    "";
                                const imageUrl = thumbnailUrl.startsWith("http")
                                    ? thumbnailUrl
                                    : `${process.env.REACT_APP_UPLOAD_URL}${thumbnailUrl}`;

                                return (
                                    <ListItemButton
                                        key={item?.documentId}
                                        onClick={() => {
                                            navigate(`/product/${item?.slug || item?.documentId}`);
                                            handleClose();
                                        }}
                                        sx={{ py: 1 }}
                                    >
                                        <ListItemAvatar>
                                            <Avatar
                                                variant="square"
                                                src={imageUrl}
                                                alt={product?.title || "product thumbnail"}
                                                sx={{ width: 56, height: 56, mr: 1 }}
                                            />
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={product?.title}
                                            primaryTypographyProps={{ noWrap: true, sx: { fontFamily: "var(--font-serif)", fontSize: "1.05rem" } }}
                                            secondaryTypographyProps={{ color: "text.secondary" }}
                                        />
                                    </ListItemButton>
                                );
                            })}
                        </List>
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
};

export default NavBarSearch;
